// About.jsx
import { Helmet } from 'react-helmet-async';
import { motion, useInView } from 'framer-motion';
import { useRef, useState } from 'react';
import {
  ShieldCheck, Lock, Users, Building2, Briefcase,
  Eye, FileCheck, BookOpen, ArrowRight, ChevronRight,
  Scale, UserCheck, Mail, MapPin, Globe, Layers,
  HeartHandshake, Compass, Target, Award
} from 'lucide-react';
import Navbar from "../Navbar/Navbar";
import Footer from "../Footer/Footer";
import './About.css';

const site = 'https://qualion.technology';

function FadeUp({ children, delay = 0, className = '' }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-60px' });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 28 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.55, delay, ease: 'easeOut' }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

export default function AboutPage() {
  const [isRoleModalOpen, setIsRoleModalOpen] = useState(false);

  const about = {
    hero: {
      eyebrow: 'ABOUT QUALION',
      headline: 'A controlled-access technical competency and assurance platform.',
      subheadline:
        'Qualion exists to make technical competence verifiable, professional development accountable, and project assurance auditable — for professionals, mentors and organisations across assured industries.',
    },
    positioning: {
      eyebrow: 'WHO WE ARE',
      headline: 'What Qualion is',
      body:
        'Qualion is a controlled-access technical competency and assurance platform for professionals, mentors and project organisations. It connects verified technical capability with structured development and auditable project assurance — under role-based, consent-controlled access.',
    },
    problem: {
      eyebrow: 'THE PROBLEM',
      title: 'Why Qualion exists',
      items: [
        { title: 'Fragmented evidence', body: 'Professional history and competency proof are scattered across documents, spreadsheets and inboxes.' },
        { title: 'Inconsistent validation', body: 'How competency is judged varies by reviewer, region and organisation — creating risk and rework.' },
        { title: 'Slow access to capability', body: 'Finding verified, appropriately qualified professionals takes too long and requires too many manual checks.' },
        { title: 'Disconnected project records', body: 'Field activity, approvals and assurance evidence live in systems that do not talk to each other.' },
      ],
    },
    vision: {
      eyebrow: 'VISION & MISSION',
      vision: {
        label: 'Vision',
        text: 'A global ecosystem where technical competence is verified, structured and portable — and where project assurance is auditable by default.',
      },
      mission: {
        label: 'Mission',
        text: 'Provide the digital infrastructure that lets professionals build verified competency, mentors guide structured development, and organisations deploy assured capability under controlled access.',
      },
    },
    audiences: {
      title: 'Who we serve',
      subtitle:
        'Three audiences. One controlled ecosystem. Detailed stakeholder types live inside approved organisation onboarding.',
      cards: [
        { icon: 'Briefcase', role: 'Technical Professionals', body: 'Build a verified competency record and progress through structured development.' },
        { icon: 'Users', role: 'Mentors', body: 'Support assigned professionals through evidence review and documented recommendations.' },
        { icon: 'Building2', role: 'Organisations', body: 'Deploy assured capability with controlled project access and auditable assurance.' },
      ],
    },
    principles: {
      eyebrow: 'OUR PRINCIPLES',
      title: 'How we build and operate',
      items: [
        { icon: 'BookOpen', title: 'Evidence before claims', body: 'Verification is grounded in documented evidence, not self-declaration.' },
        { icon: 'Lock', title: 'Role-based access', body: 'What a user can see or do depends on their verified role and entitlement.' },
        { icon: 'ShieldCheck', title: 'Consent-controlled sharing', body: 'Professional records are disclosed only where the professional has given explicit consent.' },
        { icon: 'Scale', title: 'Accountability', body: 'Significant actions are attributable, timestamped and independently reviewable.' },
        { icon: 'Compass', title: 'Continuous development', body: 'Progression is a sustained process, not a one-off certificate.' },
      ],
    },
    governance: {
      eyebrow: 'GOVERNANCE',
      title: 'Who operates the platform',
      body:
        'Qualion is operated under a defined legal entity responsible for platform integrity, data protection and access controls. Legal name, registered details and operator responsibilities are published here only after formal approval.',
      points: [
        'Platform is operated by a named legal entity responsible for data protection and access control.',
        'Role-based entitlement governs every workspace, dataset and disclosure.',
        'Consent records and audit trails are retained in line with applicable regulations.',
        'Relationship with OCEANSTAR Maritime is published here after formal approval.',
      ],
      note: 'For legal, privacy or security enquiries, contact legal@qualion.technology.',
    },
    contact: {
      eyebrow: 'CONTACT',
      title: 'Talk to us',
      body:
        'General enquiries, partnership conversations and role-specific questions are routed through the same controlled access service.',
      items: [
        { icon: 'Mail', label: 'info@qualion.technology', href: 'mailto:info@qualion.technology' },
        { icon: 'Scale', label: 'legal@qualion.technology', href: 'mailto:legal@qualion.technology' },
        { icon: 'Globe', label: 'qualion.technology', href: 'https://qualion.technology' },
        { icon: 'MapPin', label: 'India — serving global industries' },
      ],
    },
    cta: {
      eyebrow: 'CONTROLLED ACCESS',
      headline: 'Join the Qualion ecosystem',
      subheadline:
        'Every path enters the same controlled identity and verification service. Approval is not automatic.',
      primary: 'Join Ecosystem',
      secondary: 'Sign In',
    },
  };

  const iconMap = {
    Briefcase, Users, Building2, BookOpen, Lock, ShieldCheck,
    Scale, Compass, Mail, Globe, MapPin, Eye, FileCheck,
    UserCheck, Target, Award, Layers, HeartHandshake,
  };

  const handleJoin = () => setIsRoleModalOpen(true);

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    '@id': `${site}/about#webpage`,
    name: 'About Qualion — A controlled-access technical competency and assurance platform',
    url: `${site}/about`,
    isPartOf: { '@id': `${site}/#website` },
    about: { '@id': `${site}/#organization` },
  };

  return (
    <>
      <Helmet>
        <title>About Qualion — Purpose, Principles and Governance</title>
        <meta
          name="description"
          content="Qualion is a controlled-access technical competency and assurance platform. Learn our purpose, principles, governance and how access works."
        />
        <link rel="canonical" href={`${site}/about`} />
        <meta property="og:title" content="About Qualion — Purpose, Principles and Governance" />
        <meta property="og:description" content="Controlled-access technical competency and assurance for professionals, mentors and organisations." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={`${site}/about`} />
        <meta name="twitter:card" content="summary_large_image" />
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>

      <Navbar />

      <main>
        {/* ── HERO ── */}
        <section className="abt-masthead">
          <div className="abt-masthead-rule" />
          <div className="abt-masthead-grid" />
          <div className="container">
            <motion.div
              initial={{ opacity: 0, y: 32 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, ease: 'easeOut' }}
              className="abt-masthead-body"
            >
              <div className="abt-kicker abt-kicker-light">
                <div className="abt-kicker-bar" />
                <span>{about.hero.eyebrow}</span>
              </div>
              <h1 className="abt-masthead-title">{about.hero.headline}</h1>
              <p className="abt-masthead-lede">{about.hero.subheadline}</p>

              <div className="abt-masthead-ctas">
                <button
                  type="button"
                  className="abt-btn abt-btn-primary"
                  onClick={handleJoin}
                >
                  Join Ecosystem <ArrowRight size={16} />
                </button>
                <a href="/login" className="abt-btn abt-btn-ghost">
                  Sign In
                </a>
              </div>
            </motion.div>
          </div>
        </section>

        {/* ── POSITIONING ── */}
        <section className="abt-band abt-band-identity">
          <div className="container">
            <FadeUp className="abt-identity-copy">
              <div className="abt-kicker">
                <div className="abt-kicker-bar" />
                <span>{about.positioning.eyebrow}</span>
              </div>
              <h2 className="abt-h2">{about.positioning.headline}</h2>
              <p className="abt-lede">{about.positioning.body}</p>
            </FadeUp>
          </div>
        </section>

        {/* ── PROBLEM ── */}
        <section className="abt-band abt-band-problem">
          <div className="container">
            <FadeUp className="abt-band-head">
              <div className="abt-kicker">
                <div className="abt-kicker-bar" />
                <span>{about.problem.eyebrow}</span>
              </div>
              <h2 className="abt-h2">{about.problem.title}</h2>
            </FadeUp>
            <div className="abt-problem-grid">
              {about.problem.items.map((item, i) => (
                <FadeUp key={item.title} delay={i * 0.06}>
                  <div className="abt-tile abt-tile-problem">
                    <h3 className="abt-tile-title">{item.title}</h3>
                    <p className="abt-tile-body">{item.body}</p>
                  </div>
                </FadeUp>
              ))}
            </div>
          </div>
        </section>

        {/* ── VISION & MISSION ── */}
        <section className="abt-band abt-band-north">
          <div className="container">
            <FadeUp className="abt-band-head abt-band-head-center">
              <div className="abt-kicker abt-kicker-center">
                <div className="abt-kicker-bar" />
                <span>{about.vision.eyebrow}</span>
                <div className="abt-kicker-bar" />
              </div>
            </FadeUp>

            <div className="abt-north-grid">
              <FadeUp>
                <div className="abt-north-card">
                  <div className="abt-north-icon">
                    <Eye size={22} />
                  </div>
                  <div className="abt-north-label">{about.vision.vision.label}</div>
                  <p className="abt-north-text">{about.vision.vision.text}</p>
                </div>
              </FadeUp>
              <FadeUp delay={0.1}>
                <div className="abt-north-card">
                  <div className="abt-north-icon">
                    <Target size={22} />
                  </div>
                  <div className="abt-north-label">{about.vision.mission.label}</div>
                  <p className="abt-north-text">{about.vision.mission.text}</p>
                </div>
              </FadeUp>
            </div>
          </div>
        </section>

        {/* ── AUDIENCES ── */}
        <section className="abt-band abt-band-audience">
          <div className="container">
            <FadeUp className="abt-band-head abt-band-head-center">
              <div className="abt-kicker abt-kicker-center">
                <div className="abt-kicker-bar" />
                <span>WHO WE SERVE</span>
                <div className="abt-kicker-bar" />
              </div>
              <h2 className="abt-h2">{about.audiences.title}</h2>
              <p className="abt-lede">{about.audiences.subtitle}</p>
            </FadeUp>

            <div className="abt-audience-grid">
              {about.audiences.cards.map((card, i) => {
                const Icon = iconMap[card.icon] || Briefcase;
                return (
                  <FadeUp key={card.role} delay={i * 0.08}>
                    <div className="abt-tile abt-tile-audience">
                      <div className="abt-audience-icon">
                        <Icon size={22} />
                      </div>
                      <h3 className="abt-tile-title">{card.role}</h3>
                      <p className="abt-tile-body">{card.body}</p>
                    </div>
                  </FadeUp>
                );
              })}
            </div>
          </div>
        </section>

        {/* ── PRINCIPLES ── */}
        <section className="abt-band abt-band-creed">
          <div className="container">
            <FadeUp className="abt-band-head abt-band-head-center">
              <div className="abt-kicker abt-kicker-center">
                <div className="abt-kicker-bar" />
                <span>{about.principles.eyebrow}</span>
                <div className="abt-kicker-bar" />
              </div>
              <h2 className="abt-h2">{about.principles.title}</h2>
            </FadeUp>

            <div className="abt-creed-grid">
              {about.principles.items.map((item, i) => {
                const Icon = iconMap[item.icon] || ShieldCheck;
                return (
                  <FadeUp key={item.title} delay={i * 0.05}>
                    <div className="abt-tile abt-tile-creed">
                      <div className="abt-creed-icon">
                        <Icon size={20} />
                      </div>
                      <h3 className="abt-tile-title">{item.title}</h3>
                      <p className="abt-tile-body">{item.body}</p>
                    </div>
                  </FadeUp>
                );
              })}
            </div>
          </div>
        </section>

        {/* ── GOVERNANCE ── */}
        <section className="abt-band abt-band-steward">
          <div className="container">
            <FadeUp className="abt-band-head">
              <div className="abt-kicker">
                <div className="abt-kicker-bar" />
                <span>{about.governance.eyebrow}</span>
              </div>
              <h2 className="abt-h2">{about.governance.title}</h2>
              <p className="abt-lede abt-lede-narrow">{about.governance.body}</p>
            </FadeUp>

            <FadeUp delay={0.1}>
              <ul className="abt-steward-list">
                {about.governance.points.map((point) => (
                  <li key={point} className="abt-steward-item">
                    <div className="abt-steward-tick">
                      <ShieldCheck size={13} />
                    </div>
                    <span>{point}</span>
                  </li>
                ))}
              </ul>
            </FadeUp>

            <FadeUp delay={0.15}>
              <p className="abt-steward-note">{about.governance.note}</p>
            </FadeUp>
          </div>
        </section>

        {/* ── CONTACT ── */}
        <section className="abt-band abt-band-reach">
          <div className="container">
            <div className="abt-reach-grid">
              <FadeUp>
                <div>
                  <div className="abt-kicker">
                    <div className="abt-kicker-bar" />
                    <span>{about.contact.eyebrow}</span>
                  </div>
                  <h2 className="abt-h2">{about.contact.title}</h2>
                  <p className="abt-lede">{about.contact.body}</p>
                </div>
              </FadeUp>

              <FadeUp delay={0.1}>
                <ul className="abt-reach-list">
                  {about.contact.items.map((item) => {
                    const Icon = iconMap[item.icon] || Mail;
                    const content = (
                      <>
                        <div className="abt-reach-icon">
                          <Icon size={16} />
                        </div>
                        <span>{item.label}</span>
                      </>
                    );
                    return (
                      <li key={item.label}>
                        {item.href ? (
                          <a
                            href={item.href}
                            className="abt-reach-row abt-reach-row-link"
                            target={item.href.startsWith('http') ? '_blank' : undefined}
                            rel={item.href.startsWith('http') ? 'noreferrer' : undefined}
                          >
                            {content}
                          </a>
                        ) : (
                          <div className="abt-reach-row">{content}</div>
                        )}
                      </li>
                    );
                  })}
                </ul>
              </FadeUp>
            </div>
          </div>
        </section>

        {/* ── CTA ── */}
        <section className="abt-signoff">
          <div className="abt-signoff-rule" />
          <div className="container">
            <FadeUp>
              <div className="abt-signoff-body">
                <div className="abt-kicker abt-kicker-center">
                  <div className="abt-kicker-bar" />
                  <span>{about.cta.eyebrow}</span>
                  <div className="abt-kicker-bar" />
                </div>
                <h2 className="abt-signoff-title">{about.cta.headline}</h2>
                <p className="abt-signoff-lede">{about.cta.subheadline}</p>
                <div className="abt-signoff-ctas">
                  <button
                    type="button"
                    className="abt-btn abt-btn-primary"
                    onClick={handleJoin}
                  >
                    {about.cta.primary} <ArrowRight size={16} />
                  </button>
                  <a href="/login" className="abt-btn abt-btn-ghost">
                    {about.cta.secondary}
                  </a>
                </div>
              </div>
            </FadeUp>
          </div>
        </section>
      </main>

      {isRoleModalOpen && <RoleModal onClose={() => setIsRoleModalOpen(false)} />}

      <Footer />
    </>
  );
}

/* ── Role selector modal ── */
function RoleModal({ onClose }) {
  const options = [
    { slug: 'candidate',    label: 'Technical Professional', description: 'Build a verified competency record.', icon: Briefcase },
    { slug: 'mentor',       label: 'Mentor',                 description: 'Support assigned professionals.',     icon: Users },
    { slug: 'organization', label: 'Organisation',           description: 'Request organisation access.',         icon: Building2 },
  ];

  const select = (slug) => {
    window.location.href = `/join-ecosystem?role=${slug}`;
  };

  return (
    <div
      className="rm-overlay"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div
        className="rm-sheet"
        role="dialog"
        aria-modal="true"
        aria-labelledby="rm-title"
      >
        <div className="rm-head">
          <div>
            <h2 id="rm-title" className="rm-title">Join the Ecosystem</h2>
            <p className="rm-sub">Choose how you'd like to get started.</p>
          </div>
          <button
            type="button"
            className="rm-x"
            onClick={onClose}
            aria-label="Close"
          >
            ×
          </button>
        </div>
        <div className="rm-list">
          {options.map((opt) => {
            const Icon = opt.icon;
            return (
              <button
                key={opt.slug}
                type="button"
                className="rm-row"
                onClick={() => select(opt.slug)}
              >
                <span className="rm-row-icon">
                  <Icon size={20} />
                </span>
                <span className="rm-row-body">
                  <span className="rm-row-label">{opt.label}</span>
                  <span className="rm-row-desc">{opt.description}</span>
                </span>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}